// Inkorgen (designen Admin-Inkorg, ADR 0069): månadens siffror, flikarna,
// sökningen och förfrågningarna nyast först. Tills byrån har en
// tjänstekatalog visas vägen till Kom igång överst.
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useEffect, useId, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { usePrismodeller } from '../api/queries.js';
import { data } from '../api/client.js';
import { useAuth } from '../auth/auth.js';
import {
  arFilter,
  bolagsformText,
  FILTER,
  kundnamn,
  lageChip,
  lageText,
  manadNamn,
  mottagenText,
  svarstidText,
  vantar,
  type Filter,
  type Post,
} from '../inkorg/texter.js';
import { Sida } from '../layout/Sida.js';
import { beloppText } from '../testlage/texter.js';

export function Inkorg() {
  const { api } = useAuth();
  const id = useId();
  const modeller = usePrismodeller();
  const utanKatalog = modeller.data?.length === 0;
  const [params, setParams] = useSearchParams();
  const filter: Filter = arFilter(params.get('filter')) ? (params.get('filter') as Filter) : 'all';
  const q = params.get('q') ?? '';
  const [sok, setSok] = useState(q);

  // Sökningen skickas när man slutar skriva en stund.
  useEffect(() => {
    const t = setTimeout(() => {
      const text = sok.trim();
      if (text === q) return;
      const nya = new URLSearchParams(params);
      if (text.length >= 2) nya.set('q', text);
      else nya.delete('q');
      setParams(nya, { replace: true });
    }, 300);
    return () => clearTimeout(t);
  }, [sok, q, params, setParams]);

  const statistik = useQuery({
    queryKey: ['statistik'],
    queryFn: async () => data(await api.GET('/v1/admin/stats')),
  });

  const lista = useInfiniteQuery({
    queryKey: ['inkorg', filter, q],
    queryFn: async ({ pageParam }) =>
      data(
        await api.GET('/v1/admin/inquiries', {
          params: {
            query: {
              filter,
              ...(q.length >= 2 ? { q } : {}),
              ...(pageParam ? { cursor: pageParam } : {}),
            },
          },
        }),
      ),
    initialPageParam: '',
    getNextPageParam: (sida) => sida.nextCursor ?? undefined,
  });

  const forsta = lista.data?.pages[0];
  const poster = lista.data?.pages.flatMap((s) => s.items) ?? [];
  const s = statistik.data;

  const valjFilter = (kod: Filter) => {
    const nya = new URLSearchParams(params);
    if (kod === 'all') nya.delete('filter');
    else nya.set('filter', kod);
    return `?${nya.toString()}`;
  };

  return (
    <Sida
      titel="Inkorg"
      bredvid={
        <div className="sokfalt" role="search">
          <label className="visuellt-dold" htmlFor={`${id}-sok`}>
            Sök
          </label>
          <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-4-4" />
          </svg>
          <input
            id={`${id}-sok`}
            className="in"
            type="search"
            placeholder="Sök kund, e-post eller offertnummer"
            value={sok}
            onChange={(e) => setSok(e.target.value)}
          />
        </div>
      }
    >
      {utanKatalog && (
        <section className="kort kom-igang-kort" aria-labelledby="kom-igang-rubrik">
          <h2 id="kom-igang-rubrik">Kom igång med era priser</h2>
          <p>
            Välj en startmall för tjänstekatalogen. Offerterna räknas på katalogen, så den behövs
            innan formuläret kan skicka offerter.
          </p>
          <Link className="btn btn-p" to="/kom-igang">
            Välj startmall
          </Link>
        </section>
      )}
      <dl className="nyckeltal">
        <div className="kort">
          <dt className="hjalp">Förfrågningar i {s ? manadNamn(s.month) : 'månaden'}</dt>
          <dd>{s ? s.inquiries : '–'}</dd>
        </div>
        <div className="kort">
          <dt className="hjalp">Svarstid till kund, median</dt>
          <dd>{s ? svarstidText(s.medianResponseMinutes) : '–'}</dd>
        </div>
        <div className="kort">
          <dt className="hjalp">Godkända utan ändring</dt>
          <dd>{s ? (s.approved ? `${s.approvedUnchanged} av ${s.approved}` : '–') : '–'}</dd>
        </div>
        <div className="kort">
          <dt className="hjalp">Accepterade offerter</dt>
          <dd>{s ? s.accepted : '–'}</dd>
        </div>
      </dl>
      <nav className="filter" aria-label="Filter">
        {FILTER.map((f) => (
          <Link
            key={f.kod}
            to={valjFilter(f.kod)}
            className={`filterknapp${filter === f.kod ? ' vald' : ''}`}
            aria-current={filter === f.kod ? 'page' : undefined}
          >
            {f.namn}
            {forsta && <span className="antal">{forsta.counts[f.kod]}</span>}
          </Link>
        ))}
      </nav>
      {lista.isError ? (
        <div className="meddelande" role="alert">
          Inkorgen gick inte att hämta. Ladda om sidan och försök igen.
        </div>
      ) : !lista.data ? (
        <p role="status">Hämtar inkorgen …</p>
      ) : poster.length === 0 ? (
        <div className="kort tom">
          <p>
            {q
              ? `Inget i inkorgen matchar "${q}".`
              : filter === 'all'
                ? 'Inga förfrågningar än. De hamnar här när kunder fyller i formuläret på er webbplats.'
                : 'Inget här just nu.'}
          </p>
        </div>
      ) : (
        <div
          className="kort tabellkort tabellrulle"
          tabIndex={0}
          role="region"
          aria-label="Förfrågningarna"
        >
          <table className="tabell inkorg">
            <caption className="visuellt-dold">
              {FILTER.find((f) => f.kod === filter)?.namn}
              {q ? `, sökning på ${q}` : ''}
            </caption>
            <thead>
              <tr>
                <th scope="col">Kund</th>
                <th scope="col">Förfrågan</th>
                <th scope="col" className="hoger">
                  Per månad
                </th>
                <th scope="col">Status</th>
                <th scope="col" className="hoger">
                  Mottagen
                </th>
              </tr>
            </thead>
            <tbody>
              {poster.map((p) => (
                <Rad key={p.inquiryId} post={p} />
              ))}
            </tbody>
          </table>
        </div>
      )}
      {lista.hasNextPage && (
        <button
          type="button"
          className="btn btn-s visa-fler"
          disabled={lista.isFetchingNextPage}
          onClick={() => void lista.fetchNextPage()}
        >
          {lista.isFetchingNextPage ? 'Hämtar …' : 'Visa fler'}
        </button>
      )}
    </Sida>
  );
}

function Rad({ post: p }: { post: Post }) {
  const { namn, under } = kundnamn(p.customer);
  const form = p.kind === 'quote' ? bolagsformText(p.legalForm)?.toLowerCase() : null;
  const rad2 = [under, form].filter(Boolean).join(' · ');
  return (
    <tr className={vantar(p.state) ? 'ny' : undefined}>
      <td>
        <Link className="radlank" to={`/forfragningar/${p.inquiryId}`}>
          {namn}
        </Link>
        {rad2 && <div className="hjalp">{rad2}</div>}
      </td>
      <td>{p.summary ? (p.kind === 'question' ? `"${p.summary}"` : p.summary) : '–'}</td>
      <td className={`hoger ${p.quote && p.quote.monthly.high > 0 ? 'mono' : 'hjalp'}`}>
        {p.quote && p.quote.monthly.high > 0 ? beloppText(p.quote.monthly) : '–'}
      </td>
      <td>
        <span className={`chip ${lageChip(p.state)}`}>{lageText(p.state, p.reviewReasons)}</span>
      </td>
      <td className="hoger hjalp">{mottagenText(p.receivedAt)}</td>
    </tr>
  );
}
